import React, {
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import FeatherIcon from 'feather-icons-react';
import { CompanyContext } from 'src/context';
import { useActiveBanners } from './ActiveBannersContext';
import { DismissBanner, resolveBannerChannel, TrackBannerLinkClick } from 'src/graphql/banners';
import BannerLink from './BannerLink';
import useSessionBannerDismissals from './useSessionBannerDismissals';
import './AppBanners.scss';

const syncHeaderHeight = () => {
  const header = document.querySelector('.c-header');
  const height = header?.offsetHeight || 72;
  document.documentElement.style.setProperty('--app-header-height', `${height}px`);
};

const AppBanners = ({ sidebarOffset = 0 }) => {
  const barRef = useRef(null);
  const [barHeight, setBarHeight] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const { selectedCompany } = useContext(CompanyContext) || {};
  const normalizedCompanyId = selectedCompany?.id ? String(selectedCompany.id) : '';
  const { banners } = useActiveBanners();
  const { executeMutation: dismissBanner } = DismissBanner();
  const { executeMutation: trackBannerLinkClick } = TrackBannerLinkClick();
  const { dismiss, filterVisible } = useSessionBannerDismissals(dismissBanner, normalizedCompanyId);

  const bannerItems = useMemo(() => {
    const active = banners.filter(
      (banner) => resolveBannerChannel(banner) === 'BANNER',
    );
    return filterVisible(active).sort(
      (a, b) => new Date(a.scheduledAt || 0) - new Date(b.scheduledAt || 0),
    );
  }, [banners, filterVisible]);

  useEffect(() => {
    setActiveIndex((prev) => Math.min(prev, Math.max(bannerItems.length - 1, 0)));
  }, [bannerItems.length]);

  const currentBanner = bannerItems[activeIndex] || null;
  const hasMultiple = bannerItems.length > 1;

  const goPrev = () => {
    setActiveIndex((index) => (index <= 0 ? bannerItems.length - 1 : index - 1));
  };

  const goNext = () => {
    setActiveIndex((index) => (index >= bannerItems.length - 1 ? 0 : index + 1));
  };

  const handleDismiss = () => {
    if (!currentBanner?.id) return;
    dismiss(currentBanner.id);
  };

  const handleTrackLinkClick = () => {
    if (!currentBanner?.id) return;
    trackBannerLinkClick({
      bannerId: String(currentBanner.id),
      companyId: normalizedCompanyId,
    }).catch(() => {});
  };

  useLayoutEffect(() => {
    syncHeaderHeight();
    const header = document.querySelector('.c-header');
    if (!header || typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(syncHeaderHeight);
    observer.observe(header);
    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    if (!bannerItems.length) {
      setBarHeight(0);
      document.documentElement.style.setProperty('--app-banner-height', '0px');
      return undefined;
    }

    const measure = () => {
      const height = barRef.current?.offsetHeight || 0;
      setBarHeight(height);
      document.documentElement.style.setProperty('--app-banner-height', `${height}px`);
    };

    measure();
    const rafId = requestAnimationFrame(measure);
    if (!barRef.current || typeof ResizeObserver === 'undefined') {
      return () => cancelAnimationFrame(rafId);
    }

    const observer = new ResizeObserver(measure);
    observer.observe(barRef.current);
    return () => {
      cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, [bannerItems, activeIndex, currentBanner?.id]);

  if (!currentBanner) return null;

  const barStyle = {
    left: sidebarOffset,
    width: sidebarOffset ? `calc(100% - ${sidebarOffset}px)` : '100%',
  };

  const bar = (
    <div
      ref={barRef}
      className="app-banners app-banners--fixed"
      style={barStyle}
      aria-live="polite"
    >
      <div
        className="app-banners__item app-banners__item--warning"
        role="status"
      >
        <span className="app-banners__icon" aria-hidden="true">
          <FeatherIcon icon="alert-triangle" size={18} />
        </span>
        <div className="app-banners__content">
          {currentBanner.title ? (
            <strong className="app-banners__title">{currentBanner.title}</strong>
          ) : null}
          {currentBanner.title && currentBanner.message ? (
            <span className="app-banners__separator" aria-hidden="true">—</span>
          ) : null}
          {(currentBanner.message || currentBanner.linkUrl) ? (
            <span className="app-banners__message">
              {currentBanner.message ? currentBanner.message : null}
                  <BannerLink
                    linkUrl={currentBanner.linkUrl}
                    linkLabel={currentBanner.linkLabel}
                    className="app-banners__link"
                    inline
                    onTrackClick={handleTrackLinkClick}
                  />
            </span>
          ) : null}
        </div>
        {hasMultiple ? (
          <div className="app-banners__nav">
            <button
              type="button"
              className="app-banners__nav-btn"
              aria-label="Aviso anterior"
              onClick={goPrev}
            >
              <FeatherIcon icon="chevron-left" size={16} />
            </button>
            <span className="app-banners__counter" aria-live="polite">
              {activeIndex + 1}
              {' / '}
              {bannerItems.length}
            </span>
            <button
              type="button"
              className="app-banners__nav-btn"
              aria-label="Siguiente aviso"
              onClick={goNext}
            >
              <FeatherIcon icon="chevron-right" size={16} />
            </button>
          </div>
        ) : null}
        <button
          type="button"
          className="app-banners__close"
          aria-label="Cerrar aviso"
          onClick={handleDismiss}
        >
          <FeatherIcon icon="x" size={18} />
        </button>
      </div>
    </div>
  );

  return (
    <>
      {createPortal(bar, document.body)}
      <div
        className="app-banners-spacer"
        style={{ height: barHeight }}
        aria-hidden="true"
      />
    </>
  );
};

export default AppBanners;
